/**
 * OPS 3.5 B #3: the ONE proposal composer form — sections 1–5 (client & type,
 * items, billing schedule, clauses, details) rendered IDENTICALLY by the
 * creation page (/proposals/new) and the edit modal on the proposal detail
 * page. Same fields, same layout, same order; edit mode only locks the
 * non-reassignable links (client / type / engagement) and can show a status
 * badge at the top. Validation and the payload builder are shared, so the
 * two cards cannot drift.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAccessToken, authenticatedFetch } from '../lib/auth-context.js';
import { SearchableSelect } from './SearchableSelect.js';
import { ClientSearchSelect, type ClientSearchSelectOption } from './ClientSearchSelect.js';
import { ProposalDetailsForm, type RecipientDraft } from './ProposalDetailsForm.js';
import { DateInput } from './DateInput.js';
import {
  ENGAGEMENT_OPEN_STAGES,
  type ProposalSourcesOutput,
  type ClauseLibraryItemOutput,
  type EngagementOutput,
  type ProposalType,
  type ProposalPricingMode,
  type BillingSchedule,
} from '@1financial/shared';

const CURRENCIES = ['AED', 'USD', 'GBP', 'PKR', 'SAR', 'EUR', 'INR'];
const SCHEDULES: { value: BillingSchedule; label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'SEMI_ANNUAL', label: 'Every 6 months' },
  { value: 'ANNUAL', label: 'Annual' },
];

export type DraftItem = {
  key: string;
  sourceType: 'COMPLIANCE_ITEM' | 'ENGAGEMENT_SUB_ITEM' | 'CUSTOM';
  sourceId: string | null;
  title: string;
  description: string;
  price: string; // major units, as typed
};

export type DraftClause = {
  key: string;
  libraryClauseId: string | null;
  title: string;
  body: string;
};

let keyCounter = 0;
const nextKey = () => `k${++keyCounter}`;

export async function apiError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { ok?: false; error?: { message?: string; code?: string } };
    const msg = body?.error?.message ?? body?.error?.code ?? res.statusText;
    return new Error(msg ? `${res.status}: ${msg}` : `Failed: ${res.status}`);
  } catch {
    return new Error(text || res.statusText || `Failed: ${res.status}`);
  }
}

async function getJson<T>(url: string): Promise<T> {
  const res = await authenticatedFetch(url);
  if (!res.ok) throw await apiError(res);
  const body = await res.json() as { ok: true; data: T };
  return body.data;
}

/** The compose-shaped payload the form builds; the create page POSTs it to
 *  /v1/proposals/compose as-is, the edit modal maps it onto
 *  UpdateProposalInput (feeCurrency = currency, fee derived from items). */
export type ComposerPayload = {
  type: ProposalType;
  clientId: string;
  engagementId: string | undefined;
  subject: string;
  body: string;
  currency: string;
  pricingMode: ProposalPricingMode;
  /** Minor units; set only in LUMP_SUM mode. */
  lumpSumAmount: number | undefined;
  items: {
    sourceType: DraftItem['sourceType'];
    complianceItemId: string | undefined;
    engagementSubItemId: string | undefined;
    title: string;
    description: string | undefined;
    unitPrice: number;
    ordering: number;
  }[];
  billingSchedule: BillingSchedule | undefined;
  billingStartDate: string | undefined;
  clauses: { libraryClauseId: string | undefined; title: string; body: string; ordering: number }[];
  recipients: RecipientDraft[];
  expiresAt: string;
};

export type ComposerInitial = {
  clientId: string;
  clientOption: ClientSearchSelectOption | null;
  type: ProposalType;
  engagementId: string;
  engagementLabel?: string;
  currency: string;
  pricingMode: ProposalPricingMode;
  /** Major units as typed ("" when unset); only meaningful in LUMP_SUM mode. */
  lumpSum: string;
  items: DraftItem[];
  schedule: BillingSchedule;
  startDate: string;
  clauses: DraftClause[];
  subject: string;
  body: string;
  expiresAt: string; // YYYY-MM-DD
  /** Recipients EXCLUDING the auto-added client signer (shown read-only). */
  recipients: RecipientDraft[];
};

export function ProposalComposerForm({ mode, initial, statusBadge, onSubmit, submitting, submitLabel, onCancel, error }: {
  mode: 'create' | 'edit';
  initial?: ComposerInitial;
  /** Edit-only extra: current status pill rendered at the top of the card. */
  statusBadge?: React.ReactNode;
  onSubmit: (payload: ComposerPayload) => void;
  submitting: boolean;
  submitLabel: string;
  onCancel?: () => void;
  error?: string | null;
}) {
  const locked = mode === 'edit';

  const [clientId, setClientId] = useState(initial?.clientId ?? '');
  const [type, setType] = useState<ProposalType>(initial?.type ?? 'RECURRING');
  const [engagementId, setEngagementId] = useState(initial?.engagementId ?? '');
  const [currency, setCurrency] = useState(initial?.currency ?? 'AED');
  const [pricingMode, setPricingMode] = useState<ProposalPricingMode>(initial?.pricingMode ?? 'PER_ITEM');
  const [lumpSum, setLumpSum] = useState(initial?.lumpSum ?? '');
  const [items, setItems] = useState<DraftItem[]>(initial?.items ?? []);
  const [schedule, setSchedule] = useState<BillingSchedule>(initial?.schedule ?? 'MONTHLY');
  const [startDate, setStartDate] = useState(initial?.startDate ?? '');
  const [clauses, setClauses] = useState<DraftClause[]>(initial?.clauses ?? []);
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [expiresAt, setExpiresAt] = useState(initial?.expiresAt ?? '');
  const [otherRecipients, setOtherRecipients] = useState<RecipientDraft[]>(initial?.recipients ?? []);

  // Server-side search picker — scales past the API's 100-row page cap.
  const [selectedClient, setSelectedClient] = useState<ClientSearchSelectOption | null>(initial?.clientOption ?? null);

  const engagements = useQuery({
    queryKey: ['engagements', 'composer', clientId],
    // OPS 3.7 step 2: both lanes need the engagement list — picking a client
    // auto-selects the lane (open setup engagement → ONE_OFF on it, else
    // RECURRING), so fetch regardless of the current type toggle.
    enabled: !!clientId,
    queryFn: () => getJson<{ items: EngagementOutput[] }>(`/v1/engagements?clientId=${clientId}&limit=100`),
  });

  // Lane preselection: runs once per client pick, then hands control to the
  // user (switchType / manual engagement pick set laneTouched).
  const [laneTouched, setLaneTouched] = useState(false);
  useEffect(() => {
    if (locked) return;
    setLaneTouched(false);
    setEngagementId('');
    setItems([]);
  }, [clientId, locked]);
  useEffect(() => {
    if (locked || laneTouched || !clientId || !engagements.data) return;
    const open = engagements.data.items
      .filter((e) => ENGAGEMENT_OPEN_STAGES.includes(e.pipeline))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    if (open.length > 0) {
      setType('ONE_OFF');
      setEngagementId(open[0]!.id);
    } else {
      setType('RECURRING');
      setEngagementId('');
    }
    setLaneTouched(true);
  }, [locked, laneTouched, clientId, engagements.data]);

  const sources = useQuery({
    queryKey: ['proposal-sources', clientId, type, engagementId],
    enabled: !!clientId && (type === 'RECURRING' || !!engagementId),
    queryFn: () => {
      const params = new URLSearchParams({ clientId, type });
      if (type === 'ONE_OFF') params.set('engagementId', engagementId);
      return getJson<ProposalSourcesOutput>(`/v1/proposals/sources?${params.toString()}`);
    },
  });

  const library = useQuery({
    queryKey: ['clause-library'],
    queryFn: () => getJson<{ items: ClauseLibraryItemOutput[] }>('/v1/clauses'),
  });

  function switchType(next: ProposalType) {
    setLaneTouched(true);
    setType(next);
    setItems([]);
    setEngagementId('');
  }

  function toggleSourceItem(checked: boolean, draft: Omit<DraftItem, 'key'>) {
    if (checked) {
      setItems((prev) => [...prev, { ...draft, key: nextKey() }]);
    } else {
      setItems((prev) => prev.filter((i) => !(i.sourceType === draft.sourceType && i.sourceId === draft.sourceId)));
    }
  }

  function updateItem(key: string, patch: Partial<DraftItem>) {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  }

  function moveClause(key: string, dir: -1 | 1) {
    setClauses((prev) => {
      const idx = prev.findIndex((c) => c.key === key);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap]!, next[idx]!];
      return next;
    });
  }

  const totalMinor = useMemo(
    () => items.reduce((s, i) => s + Math.round((parseFloat(i.price) || 0) * 100), 0),
    [items],
  );

  function composePayload(): ComposerPayload {
    const lump = pricingMode === 'LUMP_SUM';
    return {
      type,
      clientId,
      engagementId: type === 'ONE_OFF' ? engagementId : (engagementId || undefined),
      subject: subject.trim(),
      body,
      currency,
      pricingMode,
      lumpSumAmount: lump ? Math.round((parseFloat(lumpSum) || 0) * 100) : undefined,
      items: items.map((i, idx) => ({
        sourceType: i.sourceType,
        complianceItemId: i.sourceType === 'COMPLIANCE_ITEM' ? i.sourceId ?? undefined : undefined,
        engagementSubItemId: i.sourceType === 'ENGAGEMENT_SUB_ITEM' ? i.sourceId ?? undefined : undefined,
        title: i.title.trim(),
        description: i.description.trim() || undefined,
        // LUMP_SUM: rows are the price-less scope list (server zeroes them too).
        unitPrice: lump ? 0 : Math.round((parseFloat(i.price) || 0) * 100),
        ordering: idx,
      })),
      billingSchedule: type === 'RECURRING' ? schedule : undefined,
      billingStartDate: type === 'RECURRING' && startDate ? new Date(`${startDate}T00:00:00.000Z`).toISOString() : undefined,
      clauses: clauses.map((c, idx) => ({
        libraryClauseId: c.libraryClauseId ?? undefined,
        title: c.title.trim(),
        body: c.body.trim(),
        ordering: idx,
      })),
      recipients: otherRecipients.filter((r) => r.name.trim() && r.email.trim()),
      expiresAt: (() => {
        const d = new Date(`${expiresAt}T00:00:00.000Z`);
        d.setUTCHours(23, 59, 59, 999);
        return d.toISOString();
      })(),
    };
  }

  const validationError = (() => {
    if (!clientId) return 'Pick a client';
    if (type === 'ONE_OFF' && !engagementId) return 'Pick an engagement';
    if (items.length === 0) return 'Add at least one item';
    if (items.some((i) => !i.title.trim())) return 'Every item needs a title';
    if (pricingMode === 'PER_ITEM' && items.some((i) => i.price === '' || Number.isNaN(parseFloat(i.price)) || parseFloat(i.price) < 0)) return 'Every item needs a price (0 or more)';
    if (pricingMode === 'LUMP_SUM' && (lumpSum === '' || Number.isNaN(parseFloat(lumpSum)) || parseFloat(lumpSum) < 0)) return 'Enter the lump-sum fee (0 or more)';
    if (type === 'RECURRING' && !schedule) return 'Pick a billing schedule';
    if (!subject.trim()) return 'Subject is required';
    if (!expiresAt) return 'Expiry date is required';
    if (clauses.some((c) => !c.title.trim() || !c.body.trim())) return 'Every clause needs a title and body';
    return null;
  })();

  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  async function previewPdf() {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const res = await authenticatedFetch('/v1/proposals/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(composePayload()),
      });
      if (!res.ok) throw await apiError(res);
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setPreviewing(false);
    }
  }

  const inputCls = 'w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent disabled:opacity-60';

  const engagementOptions = (engagements.data?.items ?? []).map((e) => ({ value: e.id, label: e.title || `Engagement ${e.id.slice(0, 8)}` }));
  if (locked && engagementId && !engagementOptions.some((o) => o.value === engagementId)) {
    engagementOptions.unshift({ value: engagementId, label: initial?.engagementLabel ?? `Engagement ${engagementId.slice(0, 8)}` });
  }

  return (
    <div className="space-y-5">
      {statusBadge}

      {/* 1 · Client + type */}
      <section className="bg-bg-panel border border-border rounded-xl p-5 space-y-4">
        <h2 className="text-sm font-medium text-white">1 · Client & proposal type</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">Client <span className="text-rose-400">*</span></label>
            <ClientSearchSelect
              value={clientId}
              onChange={(v, c) => { setClientId(v); setSelectedClient(c); setItems([]); setEngagementId(''); }}
              placeholder="Select client…"
              className={inputCls}
              disabled={locked}
              initialOption={initial?.clientOption ?? undefined}
            />
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Currency <span className="text-rose-400">*</span></label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => switchType('RECURRING')}
            disabled={locked}
            className={`text-left border rounded-xl p-4 transition-colors disabled:opacity-60 ${type === 'RECURRING' ? 'border-accent bg-accent/10' : 'border-border bg-bg-card hover:bg-bg-hover'}`}
          >
            <div className="text-sm font-medium text-white">Recurring</div>
            <div className="text-xs text-muted mt-1">Ongoing compliance work (VAT filings, bookkeeping…) — one billing schedule, invoices drafted automatically after signing.</div>
          </button>
          <button
            type="button"
            onClick={() => switchType('ONE_OFF')}
            disabled={locked}
            className={`text-left border rounded-xl p-4 transition-colors disabled:opacity-60 ${type === 'ONE_OFF' ? 'border-accent bg-accent/10' : 'border-border bg-bg-card hover:bg-bg-hover'}`}
          >
            <div className="text-sm font-medium text-white">One-off</div>
            <div className="text-xs text-muted mt-1">One-time engagement work (business setup…) — invoiced once from the signed snapshot.</div>
          </button>
        </div>
        {type === 'ONE_OFF' && clientId && (
          <div>
            <label className="block text-xs text-muted mb-1">Engagement <span className="text-rose-400">*</span></label>
            <SearchableSelect
              value={engagementId}
              onChange={(v) => { setEngagementId(v); setItems([]); }}
              placeholder="Select engagement…"
              options={engagementOptions}
              className={inputCls}
              disabled={locked}
            />
            {engagements.data && engagements.data.items.length === 0 && (
              <p className="text-xs text-amber-300 mt-1">This client has no engagements yet — create one first.</p>
            )}
          </div>
        )}
      </section>

      {/* 2 · Items */}
      <section className="bg-bg-panel border border-border rounded-xl p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-white">
            2 · Items — {pricingMode === 'LUMP_SUM' ? 'tick the scope, quote one fee' : 'tick sources, then hand-price'}
          </h2>
          <div className="flex rounded-lg border border-border overflow-hidden text-xs">
            {([['PER_ITEM', 'Per-item fees'], ['LUMP_SUM', 'Lump sum']] as const).map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => setPricingMode(m)}
                className={`px-3 py-1.5 transition-colors ${pricingMode === m ? 'bg-accent text-accent-fg font-medium' : 'bg-bg-card text-muted hover:text-white'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {clientId && (type === 'RECURRING' || engagementId) && (
          <div className="border border-border rounded-lg divide-y divide-border">
            {type === 'RECURRING' && (sources.data?.complianceItems ?? []).map((ci) => {
              const checked = items.some((i) => i.sourceType === 'COMPLIANCE_ITEM' && i.sourceId === ci.id);
              const label = `${ci.serviceType}${ci.periodValue ? ` — ${ci.periodValue}` : ''}`;
              return (
                <label key={ci.id} className="flex items-center gap-3 px-3 py-2 text-sm text-white cursor-pointer hover:bg-bg-hover">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggleSourceItem(e.target.checked, {
                      sourceType: 'COMPLIANCE_ITEM',
                      sourceId: ci.id,
                      title: label,
                      description: '',
                      price: ci.estimatedFee != null ? (ci.estimatedFee / 100).toFixed(2) : '',
                    })}
                    className="w-4 h-4 accent-emerald-500"
                  />
                  <span className="flex-1">{label}</span>
                  <span className="text-xs text-muted">{ci.isRecurring ? (ci.recurrenceFrequency ?? 'recurring') : 'one-off item'} · {ci.status}</span>
                </label>
              );
            })}
            {type === 'ONE_OFF' && (sources.data?.subItems ?? []).map((si) => {
              const checked = items.some((i) => i.sourceType === 'ENGAGEMENT_SUB_ITEM' && i.sourceId === si.id);
              return (
                <label key={si.id} className="flex items-center gap-3 px-3 py-2 text-sm text-white cursor-pointer hover:bg-bg-hover">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggleSourceItem(e.target.checked, {
                      sourceType: 'ENGAGEMENT_SUB_ITEM',
                      sourceId: si.id,
                      title: si.title,
                      // OPS 3.5 #10: the service-group label stays internal — the
                      // client-facing description starts empty (PDF shows title/price).
                      description: '',
                      price: '',
                    })}
                    className="w-4 h-4 accent-emerald-500"
                  />
                  <span className="flex-1">{si.title}</span>
                  <span className="text-xs text-muted">{[si.service, si.freeZoneCode, si.status].filter(Boolean).join(' · ')}</span>
                </label>
              );
            })}
            {sources.data && sources.data.complianceItems.length === 0 && sources.data.subItems.length === 0 && (
              <div className="px-3 py-3 text-sm text-muted">
                {type === 'RECURRING'
                  ? 'No compliance items on this client yet — add them on the Compliance board first, or add custom items below.'
                  : 'No sub-items on this engagement yet — load them from the business setup catalog on the engagement page, or add custom items below.'}
              </div>
            )}
            {sources.isLoading && <div className="px-3 py-3 text-sm text-muted">Loading sources…</div>}
          </div>
        )}

        {items.length > 0 && (
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={item.key} className={`grid gap-2 items-start ${pricingMode === 'LUMP_SUM' ? 'grid-cols-[1fr_32px]' : 'grid-cols-[1fr_130px_32px]'}`}>
                <div>
                  <input
                    value={item.title}
                    onChange={(e) => updateItem(item.key, { title: e.target.value })}
                    placeholder={`Item ${idx + 1} title`}
                    className={inputCls}
                  />
                  <input
                    value={item.description}
                    onChange={(e) => updateItem(item.key, { description: e.target.value })}
                    placeholder="Optional description (shown on the PDF)"
                    className={`${inputCls} mt-1 text-xs`}
                  />
                </div>
                {pricingMode === 'PER_ITEM' && (
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.price}
                      onChange={(e) => updateItem(item.key, { price: e.target.value })}
                      placeholder="0.00"
                      className={inputCls}
                    />
                    <span className="absolute right-3 top-2.5 text-xs text-muted pointer-events-none">{currency}</span>
                  </div>
                )}
                <button onClick={() => setItems((prev) => prev.filter((i) => i.key !== item.key))} className="text-rose-400 hover:text-rose-300 text-lg leading-8" title="Remove item">×</button>
              </div>
            ))}
            <div className="flex justify-between items-center pt-1 gap-3">
              <button
                onClick={() => setItems((prev) => [...prev, { key: nextKey(), sourceType: 'CUSTOM', sourceId: null, title: '', description: '', price: '' }])}
                className="text-sm text-accent hover:underline"
              >
                + Add custom item
              </button>
              {pricingMode === 'PER_ITEM' ? (
                <div className="text-sm text-white font-medium">
                  Total{type === 'RECURRING' ? ' per period' : ''}: {currency} {(totalMinor / 100).toFixed(2)}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted whitespace-nowrap">Lump-sum fee{type === 'RECURRING' ? ' per period' : ''} <span className="text-rose-400">*</span></label>
                  <div className="relative w-40">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={lumpSum}
                      onChange={(e) => setLumpSum(e.target.value)}
                      placeholder="0.00"
                      className={inputCls}
                    />
                    <span className="absolute right-3 top-2.5 text-xs text-muted pointer-events-none">{currency}</span>
                  </div>
                </div>
              )}
            </div>
            {pricingMode === 'LUMP_SUM' && (
              <p className="text-xs text-muted">The client sees the ticked items as the included scope — no per-item prices, just the one fee.</p>
            )}
          </div>
        )}
        {items.length === 0 && (
          <button
            onClick={() => setItems((prev) => [...prev, { key: nextKey(), sourceType: 'CUSTOM', sourceId: null, title: '', description: '', price: '' }])}
            className="text-sm text-accent hover:underline"
          >
            + Add custom item
          </button>
        )}
      </section>

      {/* 3 · Billing schedule (recurring) */}
      {type === 'RECURRING' && (
        <section className="bg-bg-panel border border-border rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-medium text-white">3 · Billing schedule — one schedule for the whole proposal</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted mb-1">Schedule <span className="text-rose-400">*</span></label>
              <select value={schedule} onChange={(e) => setSchedule(e.target.value as BillingSchedule)} className={inputCls}>
                {SCHEDULES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">First invoice period starts</label>
              <DateInput value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
              <p className="text-xs text-muted mt-1">Defaults to the signing date. Invoices are generated as DRAFTS a few days before each period — never sent automatically.</p>
            </div>
          </div>
        </section>
      )}

      {/* 4 · Clauses */}
      <section className="bg-bg-panel border border-border rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-medium text-white">{type === 'RECURRING' ? '4' : '3'} · Clauses — tick from the library, edit per proposal</h2>
        <div className="border border-border rounded-lg divide-y divide-border max-h-56 overflow-y-auto">
          {(library.data?.items ?? []).map((c) => {
            const checked = clauses.some((x) => x.libraryClauseId === c.id);
            return (
              <label key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm text-white cursor-pointer hover:bg-bg-hover">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) setClauses((prev) => [...prev, { key: nextKey(), libraryClauseId: c.id, title: c.title, body: c.body }]);
                    else setClauses((prev) => prev.filter((x) => x.libraryClauseId !== c.id));
                  }}
                  className="w-4 h-4 accent-emerald-500"
                />
                <span className="flex-1">{c.title}</span>
              </label>
            );
          })}
          {library.data && library.data.items.length === 0 && (
            <div className="px-3 py-3 text-sm text-muted">The clause library is empty — add clauses in Admin → Clauses, or write custom ones below.</div>
          )}
        </div>
        {clauses.map((clause, idx) => (
          <div key={clause.key} className="border border-border rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted w-5">{idx + 1}.</span>
              <input value={clause.title} onChange={(e) => setClauses((prev) => prev.map((c) => (c.key === clause.key ? { ...c, title: e.target.value } : c)))} className={inputCls} />
              <button onClick={() => moveClause(clause.key, -1)} className="text-muted hover:text-white px-1" title="Move up">↑</button>
              <button onClick={() => moveClause(clause.key, 1)} className="text-muted hover:text-white px-1" title="Move down">↓</button>
              <button onClick={() => setClauses((prev) => prev.filter((c) => c.key !== clause.key))} className="text-rose-400 hover:text-rose-300 px-1" title="Remove clause">×</button>
            </div>
            <textarea
              value={clause.body}
              onChange={(e) => setClauses((prev) => prev.map((c) => (c.key === clause.key ? { ...c, body: e.target.value } : c)))}
              rows={3}
              className={inputCls}
            />
          </div>
        ))}
        <button
          onClick={() => setClauses((prev) => [...prev, { key: nextKey(), libraryClauseId: null, title: '', body: '' }])}
          className="text-sm text-accent hover:underline"
        >
          + Add custom clause
        </button>
      </section>

      {/* 5 · Details */}
      <section className="bg-bg-panel border border-border rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-medium text-white">{type === 'RECURRING' ? '5' : '4'} · Details</h2>
        <ProposalDetailsForm
          subject={subject}
          onSubjectChange={setSubject}
          subjectPlaceholder={selectedClient ? `${selectedClient.company ?? selectedClient.name} — ${type === 'RECURRING' ? 'recurring services' : 'engagement'} proposal` : undefined}
          body={body}
          onBodyChange={setBody}
          expiresAt={expiresAt}
          onExpiresAtChange={setExpiresAt}
          signerEmail={selectedClient?.email ?? ''}
          recipients={otherRecipients}
          onRecipientsChange={setOtherRecipients}
          inputCls={inputCls}
        />
      </section>

      {validationError && <div className="text-sm text-amber-300">{validationError}</div>}
      {error && <div className="text-sm text-rose-300">{error}</div>}
      {previewError && <div className="text-sm text-rose-300">{previewError}</div>}

      <div className="flex justify-end gap-2 pb-8">
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-sm text-muted hover:text-white px-4 py-2"
          >
            Cancel
          </button>
        )}
        <button
          onClick={() => void previewPdf()}
          disabled={!!validationError || previewing}
          className="bg-bg-card hover:bg-bg-hover border border-border text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
        >
          {previewing ? 'Rendering…' : 'Preview PDF'}
        </button>
        <button
          onClick={() => onSubmit(composePayload())}
          disabled={!!validationError || submitting}
          className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-2 rounded-lg"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}
